import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useLocationUrlSync } from '@/contexts/LocationContext';
import { useToast } from '@/hooks/use-toast';
import { formatHoursHM, getISOWeek } from '@/lib/format';
import {
  Grid3X3, Plus, Trash2, Save, Send, AlertTriangle, Building2, Users as UsersIcon, Clock, CalendarPlus,
} from 'lucide-react';

const DAYS = [
  { dow: 1, short: 'Ma' },
  { dow: 2, short: 'Di' },
  { dow: 3, short: 'Wo' },
  { dow: 4, short: 'Do' },
  { dow: 5, short: 'Vr' },
  { dow: 6, short: 'Za' },
  { dow: 7, short: 'Zo' },
];

function minutesBetween(a, b) {
  if (!a || !b) return 0;
  const [h1, m1] = a.split(':').map(Number);
  const [h2, m2] = b.split(':').map(Number);
  return h2 * 60 + m2 - (h1 * 60 + m1);
}

function breakMinutes(daily) {
  if (daily > 10) return 45;
  if (daily > 5.5) return 30;
  return 0;
}

function netHoursFor(entries) {
  let totalNet = 0;
  // We enforce single entry per emp per day, so daily total = gross
  entries.forEach((e) => {
    const gross = minutesBetween(e.start_time, e.end_time) / 60;
    if (gross <= 0) return;
    const br = breakMinutes(gross);
    totalNet += gross - br / 60;
  });
  return totalNet;
}

export default function Standaardrooster() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { slug: selectedSlug, setSlug: setSelectedSlug } = useLocationUrlSync();
  const [locations, setLocations] = useState([]);
  const [template, setTemplate] = useState(null);
  const [etag, setEtag] = useState('');
  const [employees, setEmployees] = useState([]);
  const [entries, setEntries] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [feedback, setFeedback] = useState({ type: '', msg: '' });
  const [saving, setSaving] = useState(false);
  const [rolloutOpen, setRolloutOpen] = useState(false);
  const feedbackRef = useRef(null);

  // Bootstrap: load eligible locations only.
  useEffect(() => {
    (async () => {
      const { data } = await api.get('/locations');
      const eligible = data.filter((l) => l.has_roster !== false);
      setLocations(eligible);
    })();
  }, []);

  // Fallback selection (URL-aware via context). See Roosterplanner for rationale.
  useEffect(() => {
    if (locations.length > 0 && !selectedSlug) {
      setSelectedSlug(locations[0].slug);
    }
  }, [locations, selectedSlug, setSelectedSlug]);

  const load = useCallback(async () => {
    if (!selectedSlug) return;
    setFeedback({ type: '', msg: '' });
    try {
      const [tplResp, empResp] = await Promise.all([
        api.get(`/templates/${selectedSlug}`),
        api.get(`/employees?location_slug=${selectedSlug}&status=actief`),
      ]);
      setTemplate(tplResp.data);
      setEtag(tplResp.headers.etag);
      setEntries(tplResp.data.entries || []);
      setEmployees(empResp.data);
    } catch (e) {
      setFeedback({ type: 'error', msg: 'Kon standaardrooster niet laden' });
    }
  }, [selectedSlug]);

  useEffect(() => {
    load();
  }, [load]);

  const employeesById = useMemo(() => {
    const m = {};
    employees.forEach((e) => (m[e.id] = e));
    return m;
  }, [employees]);

  function getEntry(empId, dow) {
    return entries.find((e) => e.employee_id === empId && e.day_of_week === dow);
  }

  function upsertEntry(empId, dow, start, end) {
    if (!start || !end) return;
    setEntries((prev) => {
      const without = prev.filter((e) => !(e.employee_id === empId && e.day_of_week === dow));
      return [...without, { employee_id: empId, day_of_week: dow, start_time: start, end_time: end }];
    });
  }

  function removeEntry(empId, dow) {
    setEntries((prev) => prev.filter((e) => !(e.employee_id === empId && e.day_of_week === dow)));
  }

  async function handleSave() {
    setSaving(true);
    setFeedback({ type: '', msg: '' });

    const showError = (msg) => {
      setFeedback({ type: 'error', msg });
      toast({ title: 'Opslaan mislukt', description: msg, variant: 'destructive' });
      // Scroll feedback banner in view if needed
      setTimeout(() => {
        feedbackRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    };

    // Use template.id (UUID of template) for ETag construction.
    // We avoid GET in the retry loop — backend's 409 contains `current_version`.
    const templateId = template?.id;
    let currentVersion = Number.isInteger(template?.version) ? template.version : 0;
    let lastError = null;

    for (let attempt = 0; attempt < 3; attempt += 1) {
      const etagForPut = templateId ? `"${templateId}-${currentVersion}"` : etag;
      try {
        const { data, headers } = await api.put(
          `/templates/${selectedSlug}`,
          { entries },
          { headers: { 'If-Match': etagForPut } },
        );
        setTemplate(data);
        setEntries(data.entries);
        setEtag(headers.etag);
        setFeedback({ type: 'success', msg: `Opgeslagen (versie ${data.version})` });
        toast({ title: 'Standaardrooster opgeslagen', description: `Versie ${data.version}` });
        setSaving(false);
        return;
      } catch (e) {
        lastError = e;
        const status = e.response?.status;
        const detail = e.response?.data?.detail;
        // 409/412 → grab current_version from backend response (no GET needed → no cache risk)
        if ((status === 409 || status === 412) && detail && typeof detail === 'object' && Number.isInteger(detail.current_version)) {
          if (detail.current_version === currentVersion) {
            // Same version came back → stuck
            break;
          }
          currentVersion = detail.current_version;
          setFeedback({ type: 'success', msg: `Versie ververst (v${currentVersion}), opnieuw opslaan… (poging ${attempt + 2}/3)` });
          continue;
        }
        // 422 entry conflicts → not a versioning issue, no retry
        if (status === 422 && detail?.conflicts) {
          showError(`${detail.conflicts.length} fout(en): ${detail.conflicts.map((c) => `${c.reason}`).slice(0, 3).join(', ')}`);
          setSaving(false);
          return;
        }
        // 409/412 without current_version → bail out
        if (status === 409 || status === 412) break;
        // Other errors → bail out immediately
        if (status !== 409 && status !== 412) {
          showError(typeof detail === 'string' ? detail : `Opslaan mislukt (status ${status || 'onbekend'})`);
          setSaving(false);
          return;
        }
      }
    }
    // All 3 attempts exhausted → surface clear, page-specific error
    const status = lastError?.response?.status;
    const detail = lastError?.response?.data?.detail;
    let msg;
    if (status === 409 || status === 412) {
      msg = 'Standaardrooster opslaan mislukt na 3 pogingen — de versie blijft conflicteren. Vernieuw de pagina (F5) en probeer opnieuw.';
    } else {
      msg = typeof detail === 'string' ? detail : 'Standaardrooster opslaan mislukt na meerdere pogingen.';
    }
    showError(msg);
    setSaving(false);
  }

  // Summary stats
  const stats = useMemo(() => {
    const empSet = new Set(entries.map((e) => e.employee_id));
    const totalNet = netHoursFor(entries);
    return {
      employees: empSet.size,
      shifts: entries.length,
      totalNet,
    };
  }, [entries]);

  const isDirty = useMemo(() => {
    if (!template) return false;
    const a = JSON.stringify(template.entries || []);
    const b = JSON.stringify(entries);
    return a !== b;
  }, [template, entries]);

  return (
    <div className="space-y-4" data-testid="page-standaardrooster">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Grid3X3 size={18} /> Standaardrooster
          </h2>
          <select
            value={selectedSlug}
            onChange={(e) => setSelectedSlug(e.target.value)}
            data-testid="select-location"
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm"
          >
            {locations.map((l) => (
              <option key={l.slug} value={l.slug}>{l.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setRolloutOpen(true)}
            data-testid="btn-rollout"
            disabled={!template || (template.entries || []).length === 0}
            title={isDirty ? 'Tip: sla eerst op voor exacte rollout' : ''}
            className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send size={14} /> Rol uit naar week…
          </button>
          <button
            onClick={handleSave}
            data-testid="btn-save-template"
            disabled={!isDirty || saving}
            className="flex items-center gap-1.5 rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-50"
          >
            <Save size={14} /> {saving ? 'Bezig…' : isDirty ? 'Opslaan (wijzigingen)' : 'Opgeslagen'}
          </button>
        </div>
      </div>

      {feedback.msg && (
        <div
          ref={feedbackRef}
          className={`flex items-start gap-2 rounded-md px-4 py-3 text-sm ${
            feedback.type === 'error'
              ? 'border-2 border-red-300 bg-red-50 text-red-800 shadow-sm'
              : 'bg-green-50 text-green-700'
          }`}
          data-testid="template-feedback"
          role={feedback.type === 'error' ? 'alert' : 'status'}
        >
          {feedback.type === 'error' && <AlertTriangle size={16} className="mt-0.5 shrink-0" />}
          <div className="flex-1">
            {feedback.type === 'error' && (
              <p className="font-semibold mb-0.5">Opslaan mislukt</p>
            )}
            <p>{feedback.msg}</p>
          </div>
          {feedback.type === 'error' && (
            <button
              type="button"
              onClick={() => setFeedback({ type: '', msg: '' })}
              data-testid="template-feedback-dismiss"
              className="text-red-700 hover:text-red-900 text-xs px-2 py-0.5 rounded hover:bg-red-100"
              aria-label="Sluiten"
            >
              ✕
            </button>
          )}
        </div>
      )}

      {isDirty && !saving && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="dirty-banner">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} />
            <span>Je hebt niet-opgeslagen wijzigingen in dit standaardrooster.</span>
          </div>
          <button
            type="button"
            onClick={handleSave}
            data-testid="dirty-banner-save"
            className="rounded-md bg-brand-navy px-3 py-1 text-xs font-semibold text-white hover:bg-brand-navy-light"
          >
            Nu opslaan
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-slate-500 min-w-[180px] sticky left-0 bg-slate-50 z-10">Medewerker</th>
                  {DAYS.map((d) => (
                    <th key={d.dow} className="px-2 py-2 text-center text-xs uppercase tracking-wider text-slate-500 min-w-[100px]">
                      {d.short}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employees.length === 0 ? (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-sm text-slate-500">Geen actieve medewerkers op deze locatie</td></tr>
                ) : employees.map((emp) => (
                  <tr key={emp.id} className="border-t border-slate-100 hover:bg-slate-50/30">
                    <td className="px-3 py-1.5 sticky left-0 bg-white border-r border-slate-100" data-testid={`tpl-row-${emp.personeelsnummer}`}>
                      <p className="text-sm font-medium text-slate-900 truncate">{emp.name}</p>
                      <p className="text-xs text-slate-500">{emp.role} · {emp.contract_hours || 0}u</p>
                    </td>
                    {DAYS.map((d) => {
                      const entry = getEntry(emp.id, d.dow);
                      const isEditing = editingCell?.empId === emp.id && editingCell?.dow === d.dow;
                      return (
                        <td key={d.dow} className="p-1 border-r border-slate-100 align-middle">
                          {isEditing ? (
                            <CellEditor
                              initial={entry}
                              onSave={(s, e) => { upsertEntry(emp.id, d.dow, s, e); setEditingCell(null); }}
                              onDelete={() => { removeEntry(emp.id, d.dow); setEditingCell(null); }}
                              onCancel={() => setEditingCell(null)}
                            />
                          ) : entry ? (
                            <button
                              type="button"
                              onClick={() => setEditingCell({ empId: emp.id, dow: d.dow })}
                              data-testid={`tpl-cell-${emp.personeelsnummer}-${d.dow}`}
                              className="w-full rounded bg-shift-day p-1 text-center text-xs font-medium text-slate-800 hover:bg-amber-200"
                            >
                              <p>{entry.start_time}–{entry.end_time}</p>
                              <p className="text-[10px] text-slate-600">{formatHoursHM(minutesBetween(entry.start_time, entry.end_time) / 60)}</p>
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setEditingCell({ empId: emp.id, dow: d.dow })}
                              data-testid={`tpl-cell-${emp.personeelsnummer}-${d.dow}`}
                              className="w-full rounded border border-dashed border-slate-200 px-1 py-2 text-xs text-slate-300 hover:border-brand-amber hover:text-brand-amber"
                            >
                              +
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <aside className="space-y-4 self-start">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-3">
            <h3 className="text-xs uppercase tracking-wider text-slate-500">Overzicht</h3>
            <Stat icon={UsersIcon} label="Medewerkers gepland" value={stats.employees} />
            <Stat icon={CalendarPlus} label="Shifts in template" value={stats.shifts} />
            <Stat icon={Clock} label="Totaal netto uren" value={formatHoursHM(stats.totalNet)} />
          </div>
          <div className="rounded-xl bg-brand-amber/10 border border-brand-amber/30 p-4 text-xs text-slate-700">
            <p className="font-semibold mb-1 flex items-center gap-1"><AlertTriangle size={12} /> Tip</p>
            <p>Klik op een cel om een shift toe te voegen of te wijzigen. Pauze + netto uren worden server-side berekend. Rol uit naar een week om planningen te genereren.</p>
          </div>
        </aside>
      </div>

      {rolloutOpen && (
        <RolloutDialog
          locationSlug={selectedSlug}
          onClose={(result) => {
            setRolloutOpen(false);
            if (result) setFeedback({ type: 'success', msg: `Rollout: ${result.inserted} nieuw, ${result.skipped_existing} bestaand` });
          }}
        />
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-navy/10 text-brand-navy">
        <Icon size={14} />
      </div>
      <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-base font-semibold text-slate-900 tabular-nums">{value}</p>
      </div>
    </div>
  );
}

function CellEditor({ initial, onSave, onDelete, onCancel }) {
  const [start, setStart] = useState(initial?.start_time || '08:00');
  const [end, setEnd] = useState(initial?.end_time || '17:00');
  const valid = end > start;

  return (
    <div className="rounded-md border-2 border-brand-amber bg-white p-2 space-y-1.5 shadow-md" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-1">
        <input type="time" value={start} onChange={(e) => setStart(e.target.value)} data-testid="cell-start"
          className="w-full rounded border border-slate-300 px-1 py-0.5 text-xs tabular-nums" />
        <span className="text-xs text-slate-400">–</span>
        <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="cell-end"
          className="w-full rounded border border-slate-300 px-1 py-0.5 text-xs tabular-nums" />
      </div>
      <div className="flex justify-between gap-1">
        {initial && (
          <button type="button" onClick={onDelete} data-testid="cell-delete"
            title="Verwijder shift"
            className="rounded p-1 text-red-600 hover:bg-red-50"><Trash2 size={12} /></button>
        )}
        <div className="flex gap-1 ml-auto">
          <button type="button" onClick={onCancel}
            className="rounded border border-slate-200 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-50">Annu</button>
          <button type="button" disabled={!valid} onClick={() => valid && onSave(start, end)} data-testid="cell-save"
            className="rounded bg-brand-navy px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-brand-navy-light disabled:opacity-40">✓ Toevoegen</button>
        </div>
      </div>
    </div>
  );
}

function RolloutDialog({ locationSlug, onClose }) {
  const today = getISOWeek();
  const [year, setYear] = useState(today.year);
  const [week, setWeek] = useState(today.week);
  const [overwrite, setOverwrite] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [lockStatus, setLockStatus] = useState(null);

  useEffect(() => {
    let active = true;
    api.get('/planner/week-lock-status', { params: { location_id: locationSlug, year, week } })
      .then(({ data }) => { if (active) setLockStatus(data); })
      .catch(() => { if (active) setLockStatus(null); });
    return () => { active = false; };
  }, [locationSlug, year, week]);

  async function go() {
    setRunning(true);
    setError('');
    try {
      const { data } = await api.post(`/templates/${locationSlug}/rollout?year=${year}&week=${week}&overwrite=${overwrite}`);
      setResult(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : detail?.message || 'Rollout mislukt');
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
        <h3 className="text-lg font-semibold mb-3">Standaardrooster uitrollen</h3>
        {!result ? (
          <>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Jaar</label>
                <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value))} data-testid="rollout-year"
                  className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Week (ISO)</label>
                <input type="number" min="1" max="53" value={week} onChange={(e) => setWeek(parseInt(e.target.value))} data-testid="rollout-week"
                  className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm mb-3">
              <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="rollout-overwrite" />
              Overschrijf bestaande shifts (anders worden ze overgeslagen)
            </label>
            {lockStatus?.locked && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 mb-3" data-testid="rollout-lock-warning">
                {lockStatus.reason}
              </div>
            )}
            {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 mb-3">{error}</div>}
            <div className="flex justify-end gap-2">
              <button onClick={() => onClose()} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">Annuleren</button>
              <button onClick={go} disabled={running || lockStatus?.locked} data-testid="btn-confirm-rollout"
                title={lockStatus?.locked ? lockStatus.reason : undefined}
                className="rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-50">
                {running ? 'Bezig…' : 'Uitrollen'}
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-3" data-testid="rollout-result">
            <p className="text-sm text-slate-600">Week {result.week}/{result.year} (start {result.monday}):</p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-md bg-green-50 p-2"><p className="text-xs text-green-700">Nieuw</p><p className="text-2xl font-bold text-green-900">{result.inserted}</p></div>
              <div className="rounded-md bg-blue-50 p-2"><p className="text-xs text-blue-700">Overschreven</p><p className="text-2xl font-bold text-blue-900">{result.overwritten}</p></div>
              <div className="rounded-md bg-slate-100 p-2"><p className="text-xs text-slate-700">Overgeslagen</p><p className="text-2xl font-bold text-slate-900">{result.skipped_existing}</p></div>
            </div>
            <div className="flex justify-end">
              <button onClick={() => onClose(result)} className="rounded-md bg-brand-navy px-3 py-1.5 text-sm text-white">Sluiten</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
